import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RouterState, push } from 'connected-react-router';

import {
  PlanState,
  getPlanList,
  reset,
  PlanStateType,
  ServiceIdType,
  GET_PLAN_LIST_ID,
  PlanInitialState
} from 'common/modules/domain/plan';
import {
  LOGIN_USER,
  UserInformation,
  getLogin
} from 'common/modules/domain/user';
import { Principal } from 'common/modules/app/principal';
import { GlossaryState } from 'common/modules/app/glossaries';
import { FetchState, FetchStatus } from 'common/modules/app/fetch';
import {
  PageTransitionState,
  setHomeButtonClicked
} from 'common/modules/app/pageTransition';
import { PurchaseSessionState } from 'common/modules/app/purchaseSession';
import { Glossary } from 'common/utils/glossary';
import { createQuery, SasPagePath } from 'common/utils/urls';
import { AppState } from 'pages';
import { PlanLists } from 'pages/plan-list/components/PlanLists';
import {
  loadCookie,
  deleteCookie,
  SasCookieKeys,
  saveCookie
} from 'common/utils/cookie-utils';
import {
  POST_PURCHASE_SESSION_ID,
  postPurchaseSession
} from 'common/modules/domain/purchaseSession';

/** PlanをStateからPropsに変換する */
const _convertPlans = (
  planState: PlanState,
  onClickDetail: (planId: string) => () => void,
  onClickPurchase: (
    planName: string,
    planId: string,
    payment: string
  ) => () => void
) => {
  return Object.values(planState).map((p) => ({
    imageSrc: p.imageUrl,
    planId: p.planId,
    planName: p.name,
    planDescription: p.description,
    payment: p.prices[0].payment,
    onClickDetail: onClickDetail(p.planId),
    onClickPurchase: onClickPurchase(p.name, p.planId, p.prices[0].payment)
  }));
};

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const planState = useSelector<AppState, PlanInitialState>(
    (state) => state.domain.plan
  );
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );
  const principalState = useSelector<AppState, Principal>(
    (state) => state.app.principal
  );
  const routerState = useSelector<AppState, RouterState>(
    (state) => state.app.router
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );
  const purchaseSessionState = useSelector<AppState, PurchaseSessionState>(
    (state) => state.app.purchaseSession
  );

  const isPlanListProcessing =
    fetchState[GET_PLAN_LIST_ID] !== null &&
    fetchState[GET_PLAN_LIST_ID] === FetchStatus.PROCESSING;
  const isPlanListFetched =
    fetchState[GET_PLAN_LIST_ID] !== null &&
    fetchState[GET_PLAN_LIST_ID] === FetchStatus.UNTREATED;
  const isPlanListFailed =
    fetchState[GET_PLAN_LIST_ID] !== null &&
    fetchState[GET_PLAN_LIST_ID] === FetchStatus.FAILED;
  const pageTransitionState = useSelector<AppState, PageTransitionState>(
    (state) => state.app.pageTransition
  );
  const isProcessing =
    fetchState[LOGIN_USER] == FetchStatus.PROCESSING ||
    fetchState[POST_PURCHASE_SESSION_ID] == FetchStatus.PROCESSING;
  // 購入開始処理中のプランIDの取得
  let processingPlanId = purchaseSessionState.planId;
  if (!processingPlanId) {
    // 購入開始処理中のプランIDがない場合は、CookieのプランID（ログイン処理中）を取得
    const cookie = loadCookie();
    if (cookie[SasCookieKeys.KEY_BEFORE_PURCHASE]) {
      processingPlanId = cookie[SasCookieKeys.KEY_BEFORE_PURCHASE];
    }
  }

  return {
    plan: planState,
    principal: principalState,
    user: userState,
    router: routerState,
    glossary: Glossary.instance(glState),
    isProcessingList: isPlanListProcessing,
    isFetchedList: isPlanListFetched,
    isFetchFailed: isPlanListFailed,
    isProcessing: isProcessing,
    processingPlanId: processingPlanId,
    pageTransition: pageTransitionState
  };
};

type SelectedState = ReturnType<typeof useStateProps>;
/**
 * StoreのStateとActionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const _plan = state.plan.planList;
  const _isLoggedIn = state.principal.isLoggedIn;
  /** routerStateからクエリパラメータを取得する */
  const _searchParams = new URLSearchParams(state.router.location.search);
  const _serviceId =
    _searchParams.get('serviceId') || ServiceIdType.TYPE_ALL_PLAN;
  const _modelName = _searchParams.get('model') || '';
  const _type = _searchParams.get('type') || '';
  const _country = state.user.country;
  const _language = _searchParams.get('language') || state.user.language;

  /** プラン一覧画面の初期化処理 */
  const onInit = async () => {
    // プラン一覧が未取得＋取得中では無いときのみ取得する
    if (!state.isFetchedList && !state.isProcessingList) {
      await dispatch(reset());
      await dispatch(
        await getPlanList(
          _country,
          _serviceId,
          _language,
          PlanStateType.TYPE_ACTIVE,
          _modelName,
          _type
        )
      );
      await saveCookie(SasCookieKeys.KEY_SERVICE_ID, _serviceId);
    }
    // ホームボタンによって遷移してきた場合、スクロール位置をリセットする
    // ブラウザの戻るボタンでもonInitは走るので、stateを使用して制御した
    if (state.pageTransition.isHomeButtonClicked) {
      dispatch(setHomeButtonClicked(false));
      window.scrollTo(0, 0);
    }
  };

  /** 購入ボタンからログインしたときに購入確認画面を表示 */
  const checkBeforePurchaseInfo = async (
    planId: string,
    planName: string,
    payment: string
  ) => {
    const cookie = loadCookie();
    // 購入ボタンからログインし、対象プランが存在する場合
    if (
      cookie[SasCookieKeys.KEY_BEFORE_PURCHASE] &&
      cookie[SasCookieKeys.KEY_BEFORE_PURCHASE] === planId
    ) {
      if (_isLoggedIn) {
        // ログイン済みの状態ならば購入開始APIの呼び出しと購入確認画面を表示
        deleteCookie(SasCookieKeys.KEY_BEFORE_PURCHASE);
        await dispatch(
          postPurchaseSession(
            cookie[SasCookieKeys.KEY_TOKEN],
            cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
            planId,
            planName,
            _country,
            _language,
            payment
          )
        );
      }
    }
  };

  /** プラン一覧取得が完了したらログイン後の購入確認画面表示で利用するCookieを削除 */
  const deleteBeforePurchaseInfo = () => {
    if ((state.isFetchedList || state.isFetchFailed) && _isLoggedIn) {
      // プラン一覧が取得済みまたはエラー終了、かつログイン状態ならクッキー消す
      // 対象プランが存在しなかった場合に削除する目的
      deleteCookie(SasCookieKeys.KEY_BEFORE_PURCHASE);
    }
  };

  /** 詳細をみるクリック時の処理 */
  const onClickDetail = (planId: string) => () => {
    const _query = createQuery([
      { key: 'planId', value: planId },
      { key: 'serviceId', value: _searchParams.get('serviceId') },
      { key: 'language', value: _searchParams.get('language') }
    ]);
    dispatch(push(`${SasPagePath.PlanDetail}${_query}`));
  };

  /** 購入ボタンクリック時の処理 */
  const onClickPurchase = (
    planName: string,
    planId: string,
    payment: string
  ) => async () => {
    if (!state.isProcessing) {
      if (_isLoggedIn) {
        // 購入開始APIを呼び出し、購入確認画面を表示
        // 購入確認画面のボタン押下時に呼び出すとSafariでブロックされるためここで実施
        const cookie = loadCookie();
        await dispatch(
          postPurchaseSession(
            cookie[SasCookieKeys.KEY_TOKEN],
            cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
            planId,
            planName,
            _country,
            _language,
            payment
          )
        );
      } else {
        // ログイン後に購入確認画面を表示するようにCookieに情報を記録
        saveCookie(SasCookieKeys.KEY_BEFORE_PURCHASE, planId);
        dispatch(getLogin());
      }
    }
  };

  return {
    plans: _convertPlans(_plan, onClickDetail, onClickPurchase),
    initializePage: onInit,
    checkBeforePurchaseInfo: checkBeforePurchaseInfo,
    deleteBeforePurchaseInfo: deleteBeforePurchaseInfo,
    isSelectedCountry: !!_country,
    select: {
      country: _country,
      language: _language,
      serviceId: _serviceId
    },
    glossary: state.glossary,
    isProcessingList: state.isProcessingList,
    processingPlanId: state.processingPlanId
  };
};

/**
 * プランリストのContainer Component
 */
export default function ContainerComponent() {
  const _props = { ...useMargeProps(useStateProps()) };
  return <PlanLists {..._props} />;
}
